import { useMemo, useState } from 'react';

import type {
  LookalikePollAnswer,
  LookalikePollClusterResult,
  LookalikePollResult,
  LookalikeView,
} from '@repo/contracts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeadLabel,
  TableRow,
  TableWrap,
} from '@repo/ui/table';

import { slotChipClass } from './lookalike-colors';
import { THIN_SCROLLBAR } from './lookalike-scrollbar';

/** `Math.round((part / total) * 100)` looks equivalent but isn't reliably —
 *  dividing first can land on a binary fraction that's a hair under the true
 *  value (23/40 stores as 0.5749999999999999…), and `* 100` carries that
 *  error across the rounding boundary: 57%, not the correct 58%. Multiplying
 *  before dividing keeps the numerator exact up to the one division that
 *  actually needs to happen, which is what avoids it. */
function pctOf(part: number, total: number): number {
  return total > 0 ? Math.round((part * 100) / total) : 0;
}

/** Also used by the research-room chat's compact lookalike-poll card. */
export function YesNoBar({
  no,
  unknown,
  yes,
}: {
  no: number;
  unknown: number;
  yes: number;
}) {
  const judged = yes + no;
  const yesPct = pctOf(yes, judged);
  const noPct = pctOf(no, judged);
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-ui-strong font-semibold text-success-ink">
          {judged > 0 ? `${yesPct}%` : '—'} yes
        </span>
        <span className="text-ui-strong font-semibold text-critical">
          {judged > 0 ? `${noPct}%` : '—'} no
        </span>
      </div>
      <div className="mt-1.5 flex h-2.5 overflow-hidden rounded-pill bg-hairline">
        {judged > 0 ? (
          <>
            <div className="bg-success" style={{ width: `${yesPct}%` }} />
            <div className="bg-critical" style={{ width: `${noPct}%` }} />
          </>
        ) : null}
      </div>
      <p className="mt-1.5 font-mono text-caption text-text-muted">
        {yes.toLocaleString()} yes · {no.toLocaleString()} no
        {unknown > 0 ? ` · ${unknown.toLocaleString()} unknown` : ''}
      </p>
    </div>
  );
}

/** Also used by the research-room chat's compact lookalike-poll card. */
export function ClusterBreakdown({
  clusters,
}: {
  clusters: LookalikePollClusterResult[];
}) {
  if (clusters.length < 2) return null;
  return (
    <div className="mt-5">
      <p className="text-small-label text-text-muted uppercase">By cluster</p>
      <div className="mt-2 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
        {clusters.map((cluster) => (
          <div
            className="rounded-card border border-hairline bg-warm-alt p-3"
            key={cluster.index}
          >
            <div className="flex items-center gap-2">
              <span
                className={`size-2.5 shrink-0 rounded-pill ${slotChipClass(cluster.index)}`}
              />
              <span className="min-w-0 flex-1 truncate text-ui-strong font-semibold text-text-primary">
                {cluster.label}
              </span>
            </div>
            <div className="mt-2">
              <YesNoBar
                no={cluster.no}
                unknown={cluster.unknown}
                yes={cluster.yes}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const ANSWER_RANK: Record<LookalikePollAnswer['answer'], number> = {
  no: 1,
  unknown: 2,
  yes: 0,
};

const ANSWER_BADGE_CLASS: Record<LookalikePollAnswer['answer'], string> = {
  no: 'bg-critical/15 text-critical',
  unknown: 'bg-hairline text-text-muted',
  yes: 'bg-success/25 text-success-ink',
};

const ANSWER_LABEL: Record<LookalikePollAnswer['answer'], string> = {
  no: 'No',
  unknown: 'Unknown',
  yes: 'Yes',
};

function AnswerBadge({ answer }: { answer: LookalikePollAnswer['answer'] }) {
  return (
    <span
      className={`inline-flex h-6 items-center rounded-pill px-2.5 text-caption font-semibold ${ANSWER_BADGE_CLASS[answer]}`}
    >
      {ANSWER_LABEL[answer]}
    </span>
  );
}

/**
 * Every lookalike's own verdict, one row each — the detail behind the
 * aggregate split above. `result.answers` carries only the verdict; the
 * profile fields come from joining back against `view.lookalikes` by id, so
 * the wire payload doesn't duplicate a profile the client already has.
 *
 * Sorted yes-first, then no, then unknown, and by confidence within each
 * group — the point of a per-persona table is scanning for *who* leans which
 * way, not re-deriving population order.
 */
function AnswerTable({
  answers,
  clusters,
  lookalikes,
}: {
  answers: LookalikePollAnswer[];
  clusters: LookalikeView['clusters'];
  lookalikes: LookalikeView['lookalikes'];
}) {
  const clusterLabels = useMemo(
    () => new Map(clusters.map((cluster) => [cluster.index, cluster.label])),
    [clusters],
  );
  const lookalikeById = useMemo(
    () => new Map(lookalikes.map((lookalike) => [lookalike.id, lookalike])),
    [lookalikes],
  );
  const rows = useMemo(
    () =>
      [...answers]
        .filter((answer) => lookalikeById.has(answer.id))
        .sort((a, b) => {
          const rankDiff = ANSWER_RANK[a.answer] - ANSWER_RANK[b.answer];
          if (rankDiff !== 0) return rankDiff;
          return (b.confidence ?? 0) - (a.confidence ?? 0);
        }),
    [answers, lookalikeById],
  );

  return (
    <div className="mt-5">
      <p className="text-small-label text-text-muted uppercase">
        Every lookalike&rsquo;s answer
      </p>
      <TableWrap
        className={`mt-2 max-h-96 border-hairline shadow-soft ${THIN_SCROLLBAR}`}
      >
        <Table>
          <TableHeader>
            <TableRow className="border-hairline">
              <TableHead className="border-hairline">
                <TableHeadLabel>Answer</TableHeadLabel>
              </TableHead>
              <TableHead className="border-hairline">
                <TableHeadLabel>Job title</TableHeadLabel>
              </TableHead>
              <TableHead className="border-hairline">
                <TableHeadLabel>Company size</TableHeadLabel>
              </TableHead>
              <TableHead className="border-hairline">
                <TableHeadLabel>Location</TableHeadLabel>
              </TableHead>
              <TableHead className="border-hairline">
                <TableHeadLabel>Experience</TableHeadLabel>
              </TableHead>
              <TableHead className="border-hairline">
                <TableHeadLabel>Cluster</TableHeadLabel>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((answer) => {
              const lookalike = lookalikeById.get(answer.id)!;
              return (
                <TableRow key={answer.id}>
                  <TableCell>
                    <AnswerBadge answer={answer.answer} />
                  </TableCell>
                  <TableCell>{lookalike.jobTitle}</TableCell>
                  <TableCell>{lookalike.companySize}</TableCell>
                  <TableCell>{lookalike.location}</TableCell>
                  <TableCell className="font-mono tabular-nums">
                    {lookalike.experienceYears} yrs
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1.5">
                      <span
                        className={`size-2 shrink-0 rounded-pill ${slotChipClass(lookalike.cluster)}`}
                      />
                      {clusterLabels.get(lookalike.cluster) ?? '—'}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableWrap>
    </div>
  );
}

/**
 * A yes/no question, asked of every lookalike via Jev — an experiment, not a
 * persisted feature: nothing here is saved, and asking again replaces the
 * result on screen rather than adding to a history. Each lookalike is judged
 * independently in the voice of its own sampled profile — see
 * `LookalikePollResult`'s own doc.
 */
export function LookalikesPoll({
  error,
  onAsk,
  polling,
  result,
  view,
}: {
  error: string | null;
  onAsk: (question: string) => void;
  polling: boolean;
  result: LookalikePollResult | null;
  view: LookalikeView;
}) {
  const [question, setQuestion] = useState('');

  return (
    <div>
      <div className="mx-auto max-w-[560px]">
        <p className="text-small-label text-text-muted uppercase">
          Poll the population
        </p>
        <p className="mt-1.5 text-caption leading-relaxed text-text-secondary">
          A yes/no question, fanned out to all{' '}
          {view.lookalikes.length.toLocaleString()} lookalikes via Jev — each
          answers independently, in the voice of their own profile. Nothing here
          is saved; asking again replaces the result below.
        </p>

        <div className="mt-4 flex items-center gap-2">
          <input
            className="h-9 flex-1 rounded-button bg-warm-alt px-3 text-caption text-text-primary outline-none placeholder:text-text-muted focus:ring-2 focus:ring-ink"
            disabled={polling}
            onChange={(event) => {
              setQuestion(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && question.trim().length >= 3) {
                onAsk(question.trim());
              }
            }}
            placeholder="e.g. Would you pay $20/month for this?"
            type="text"
            value={question}
          />
          <button
            className="h-9 shrink-0 cursor-pointer rounded-button bg-brand px-4 text-ui font-semibold text-white transition hover:bg-brand-alt active:scale-[0.98] disabled:cursor-default disabled:opacity-50"
            disabled={polling || question.trim().length < 3}
            onClick={() => {
              onAsk(question.trim());
            }}
            type="button"
          >
            {polling ? 'Asking…' : 'Ask Jev'}
          </button>
        </div>

        {error ? (
          <p className="mt-3 text-caption text-critical" role="alert">
            {error}
          </p>
        ) : null}

        {result ? (
          <div className="mt-6 rounded-card border border-hairline bg-surface p-4 shadow-soft">
            <p className="text-caption text-text-muted" title={result.question}>
              &ldquo;{result.question}&rdquo;
            </p>
            <div className="mt-3">
              <YesNoBar
                no={result.no}
                unknown={result.unknown}
                yes={result.yes}
              />
            </div>
            <ClusterBreakdown clusters={result.clusters} />
          </div>
        ) : null}
      </div>

      {result ? (
        <AnswerTable
          answers={result.answers}
          clusters={view.clusters}
          lookalikes={view.lookalikes}
        />
      ) : null}
    </div>
  );
}
